// People → Recruiting (owner preview: lib/ownerPreview.ts — People.tsx's
// PeopleRecruiting renders this only for Tomer; everyone else keeps the
// unchanged "Coming soon" card). Read-only pass: no write handlers anywhere
// in this tree (the action chips on the candidate card are disabled).
//
// docs/RECRUITING-PLAN.md, "What the board is" / "Beyond Monday" is the
// build's source of truth; this file renders exactly the six numbered pieces
// the build spec lists (clinic strip, day view, pipeline counts, search,
// table, card) against the API in lib/recruitingApi.ts.
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useResource, refetchResource } from '../../lib/store';
import { session } from '../../lib/session';
import { ClinicTabs, visibleClinics, activeClinicFrom } from '../ClinicTabs';
import type { LocationCode } from '../../lib/locations';
import {
  fetchRecruitingSummary, RECRUITING_SUMMARY_KEY, RECRUITING_STAGES, STAGE_LABEL,
  fetchRecruitingCandidates, recruitingCandidatesKey, RECRUITING_CANDIDATES_DEFAULT_LIMIT,
  type RecruitingStage, type RecruitingClinicSummary, type RecruitingLocationParam,
  type RecruitingSummary,
} from '../../lib/recruitingApi';
import { CandidateTable } from './CandidateTable';
import { CandidateCard } from './CandidateCard';
import { AddCandidateForm } from './actions/AddCandidateForm';
import { RecruitingTemplates } from './RecruitingTemplates';
import { RecruitingGuide } from './RecruitingGuide';
import { SameNameReview } from './SameNameReview';
import type { RecruitingViewer } from './actions/CandidateActions';

export const RECRUITING_ALL_TAB_ID = 'ALL_CLINICS';
export const RECRUITING_ARCHIVE_TAB_ID = 'ARCHIVE';
/** Owner, 2026-09-28: email templates live behind a Templates link beside
 * All clinics, not as a box at the top of the board. */
export const RECRUITING_TEMPLATES_TAB_ID = 'TEMPLATES';
/** Owner, 2026-10-06: the nvRecruiter guide is a tab here, next to Archive
 * (not a top-menu item). `/recruiting?tab=guide` opens straight onto it. */
export const RECRUITING_GUIDE_TAB_ID = 'GUIDE';
/** Owner, 2026-10-11: the same-name review list — owner/director only. */
export const RECRUITING_SAME_NAME_TAB_ID = 'SAME_NAME';

const SEARCH_DEBOUNCE_MS = 300;

/** A candidate's page is a real address — `/recruiting?candidate=123` — so a
 * refresh, the Back button and a link from the digest all land on the person
 * (owner, 2026-09-25: "nothing there that allows you to click on a
 * candidate's page where it opens up their page showing me their resume"). */
function candidateIdFromUrl(): number | null {
  const m = /[?&]candidate=(\d+)/.exec(window.location.search);
  return m ? Number(m[1]) : null;
}

function candidatePath(id: number | null): string {
  return id === null ? '/recruiting' : `/recruiting?candidate=${id}`;
}

function locationParamFor(tab: string): RecruitingLocationParam {
  if (tab === RECRUITING_ALL_TAB_ID) return 'ALL';
  if (tab === RECRUITING_ARCHIVE_TAB_ID) return 'ARCHIVE';
  return tab as LocationCode;
}

function clinicSummaryFor(data: RecruitingSummary | null, tab: string): RecruitingClinicSummary | null {
  if (!data) return null;
  if (tab === RECRUITING_ALL_TAB_ID) return data.all;
  if (tab === RECRUITING_ARCHIVE_TAB_ID) return null;
  return data.clinics[tab as LocationCode] ?? null;
}

/** New today / To review / Interviews / Offers out / LMTs on the roster —
 * "the manager's day view first" (RECRUITING-PLAN.md). Four tiles filter the
 * table ("Clicking To review / Interviews / Offers filters the table"; New
 * today added by the owner 2026-09-30: "make the new today clickable") —
 * only the headcount tile is a plain number. */
function DayView(
  {
    summary, stageFilter, interviewsFilter, newTodayFilter, onToggleStage, onToggleInterviews, onToggleNewToday,
  }: {
    summary: RecruitingClinicSummary | null;
    stageFilter: RecruitingStage | null;
    interviewsFilter: boolean;
    newTodayFilter: boolean;
    onToggleStage: (stage: RecruitingStage) => void;
    onToggleInterviews: () => void;
    onToggleNewToday: () => void;
  },
): JSX.Element {
  const newToday = summary?.newToday ?? 0;
  const toReview = summary?.stages.TO_REVIEW ?? 0;
  const interviews = summary ? summary.stages.INTERVIEW + summary.stages.PRACTICAL : 0;
  const offers = summary?.stages.OFFER ?? 0;
  const lmt = summary?.lmtHeadcount ?? null;

  return (
    <div class="recruiting-tiles" data-testid="recruiting-day-view">
      <button
        type="button"
        class="recruiting-tile recruiting-tile-btn"
        data-testid="recruiting-tile-new-today"
        aria-pressed={newTodayFilter}
        onClick={onToggleNewToday}
      >
        <div class="recruiting-tile-num">{newToday}</div>
        <div class="recruiting-tile-label">New today</div>
      </button>
      <button
        type="button"
        class="recruiting-tile recruiting-tile-btn"
        data-testid="recruiting-tile-to-review"
        aria-pressed={stageFilter === 'TO_REVIEW'}
        onClick={() => onToggleStage('TO_REVIEW')}
      >
        <div class="recruiting-tile-num">{toReview}</div>
        <div class="recruiting-tile-label">To review</div>
      </button>
      <button
        type="button"
        class="recruiting-tile recruiting-tile-btn"
        data-testid="recruiting-tile-interviews"
        aria-pressed={interviewsFilter}
        onClick={onToggleInterviews}
      >
        <div class="recruiting-tile-num">{interviews}</div>
        <div class="recruiting-tile-label">Interviews</div>
      </button>
      <button
        type="button"
        class="recruiting-tile recruiting-tile-btn"
        data-testid="recruiting-tile-offers"
        aria-pressed={stageFilter === 'OFFER'}
        onClick={() => onToggleStage('OFFER')}
      >
        <div class="recruiting-tile-num">{offers}</div>
        <div class="recruiting-tile-label">Offers out</div>
      </button>
      <div class="recruiting-tile" data-testid="recruiting-tile-lmt-headcount">
        <div class="recruiting-tile-num">{lmt == null ? '—' : lmt}</div>
        <div class="recruiting-tile-label">LMTs on the roster</div>
      </div>
    </div>
  );
}

/** One chip per stage, in the fixed order the API returns them. TO_REVIEW
 * gets the warn treatment when it is above zero — "an untouched row must
 * visibly ask for attention" (RECRUITING-PLAN.md). */
function PipelineChips(
  { summary, stageFilter, onToggle }: {
    summary: RecruitingClinicSummary | null;
    stageFilter: RecruitingStage | null;
    onToggle: (stage: RecruitingStage) => void;
  },
): JSX.Element {
  return (
    <div class="recruiting-chips" data-testid="recruiting-pipeline">
      {RECRUITING_STAGES.map((stage) => {
        const count = summary?.stages[stage] ?? 0;
        const highlight = stage === 'TO_REVIEW' && count > 0;
        return (
          <button
            key={stage}
            type="button"
            class={`btn recruiting-chip${highlight ? ' warn' : ''}`}
            aria-pressed={stageFilter === stage}
            data-testid={`recruiting-stage-chip-${stage}`}
            onClick={() => onToggle(stage)}
          >
            {STAGE_LABEL[stage]} {count}
          </button>
        );
      })}
    </div>
  );
}

export function RecruitingBoard(): JSX.Element {
  const clinics = visibleClinics();
  // A link from the Desk may name the tab: `?clinic=ALL` or a clinic code
  // (the Desk's New today opens the same clinic scope its panel shows).
  const [tab, setTab] = useState<string>(() => {
    const m = /[?&]clinic=([A-Z_]+)/.exec(window.location.search);
    if (m && m[1] === 'ALL' && clinics.length > 1) return RECRUITING_ALL_TAB_ID;
    return activeClinicFrom(m ? m[1] : null, clinics);
  });
  const [stageFilter, setStageFilter] = useState<RecruitingStage | null>(null);
  // The API's `stage` param takes exactly one value, but "Interviews" on the
  // day view is INTERVIEW + PRACTICAL together (same sum the tile's own
  // number uses). Rather than change the contract, this one filter is
  // resolved CLIENT side: no `stage` is sent, the page is fetched wider, and
  // the two stages are kept from what came back. Spec-silent judgment call —
  // flagged in the build report.
  const [interviewsFilter, setInterviewsFilter] = useState(false);
  // New today — also opened straight from the Desk's "New today" number
  // (`/recruiting?new=today`).
  const [newTodayFilter, setNewTodayFilter] = useState(() => /[?&]new=today\b/.test(window.location.search));
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [limit, setLimit] = useState(RECRUITING_CANDIDATES_DEFAULT_LIMIT);
  // The open candidate — their page replaces the list. Comes from the
  // address on arrival, so a refresh or a shared link lands on the person.
  const [selectedId, setSelectedId] = useState<number | null>(() => candidateIdFromUrl());
  // Which side of the Archive line the open card came from: the Archive tab
  // lists only people with an outcome, every other tab only people without
  // one, so the tab at the click IS the side. Fixed for the life of the
  // selection — a card whose person is archived while open keeps its key
  // and refetches in place; the next visit drops it with its side
  // (lib/dataVersions.ts). Null for a card opened without a row on screen.
  const [selectedArchived, setSelectedArchived] = useState<boolean | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // A filter scoped to one clinic's pipeline (or an outcome-only Archive
  // view) does not carry meaning across a tab change — same reasoning
  // PayHours.tsx's own clinic switcher never carries a raise pill's expanded
  // state across clinics. Not on mount: a candidate opened from the address
  // must survive the first render.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    setStageFilter(null);
    setInterviewsFilter(false);
    setNewTodayFilter(false);
    setSelectedId(null);
    setLimit(RECRUITING_CANDIDATES_DEFAULT_LIMIT);
  }, [tab]);

  // The browser's Back / Forward buttons move between the list and a
  // person's page, because opening one pushes a real address.
  useEffect(() => {
    const onPop = (): void => setSelectedId(candidateIdFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function openCandidate(id: number, side: boolean | null): void {
    setSelectedArchived(side);
    setSelectedId(id);
    if (candidateIdFromUrl() !== id) window.history.pushState({}, '', candidatePath(id));
  }

  function backToList(): void {
    setSelectedId(null);
    if (candidateIdFromUrl() !== null) window.history.pushState({}, '', candidatePath(null));
  }

  useEffect(() => {
    setLimit(RECRUITING_CANDIDATES_DEFAULT_LIMIT);
  }, [stageFilter, interviewsFilter, newTodayFilter, debouncedQuery]);

  const summary = useResource(RECRUITING_SUMMARY_KEY, fetchRecruitingSummary);
  // Who is acting. A manager's clinic is the one they are mapped to; the
  // server decides every write anyway (SPEC-001 §6) — this only chooses
  // which chips are worth drawing.
  const me = session.value.me;
  const viewer: RecruitingViewer | null = me && me.role
    ? { role: me.role, locationCode: (me.role === 'MANAGER' ? ((me.locations && me.locations[0]) ?? null) : null) as RecruitingViewer['locationCode'], email: me.email }
    : null;
  const [adding, setAdding] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(() => /[?&]tab=guide\b/i.test(window.location.search));
  const [sameNameOpen, setSameNameOpen] = useState(false);
  const canReviewSameName = viewer?.role === 'OWNER' || viewer?.role === 'DIRECTOR';
  const sideOpen = templatesOpen || guideOpen || (sameNameOpen && canReviewSameName);
  const clinicSummary = clinicSummaryFor(summary.status === 'ready' ? summary.data : null, tab);

  const archive = tab === RECRUITING_ARCHIVE_TAB_ID;
  // A search term looks at everyone — every clinic, active and closed (owner,
  // 2026-11-02). The server ignores clinic/archive/stage filters then.
  const searching = debouncedQuery.trim() !== '';
  const location = searching ? 'ALL' : locationParamFor(tab);
  const stageParam = searching || interviewsFilter ? null : stageFilter;
  const fetchLimit = interviewsFilter ? Math.max(limit, 200) : limit;
  const newToday = newTodayFilter && !archive && !searching;
  const candidatesKey = recruitingCandidatesKey({
    location, stage: stageParam, newToday, q: debouncedQuery, limit: fetchLimit, offset: 0,
  });
  const candidates = useResource(
    candidatesKey,
    () => fetchRecruitingCandidates({ location, stage: stageParam, newToday, q: debouncedQuery, limit: fetchLimit, offset: 0 }),
  );

  /** After any write: fresh counts and rows, from the server, never patched
   * in place — every number on the screen is counted from rows. */
  function refreshBoard(): void {
    void refetchResource(RECRUITING_SUMMARY_KEY, fetchRecruitingSummary);
    void refetchResource(candidatesKey, () => fetchRecruitingCandidates({ location, stage: stageParam, newToday, q: debouncedQuery, limit: fetchLimit, offset: 0 }));
  }

  const rowsRaw = candidates.status === 'ready' ? candidates.data.rows : [];
  const rows = interviewsFilter && !searching ? rowsRaw.filter((r) => r.stage === 'INTERVIEW' || r.stage === 'PRACTICAL') : rowsRaw;
  const total = interviewsFilter && !searching
    ? (clinicSummary ? clinicSummary.stages.INTERVIEW + clinicSummary.stages.PRACTICAL : rows.length)
    : (candidates.status === 'ready' ? candidates.data.total : 0);
  const canLoadMore = (!interviewsFilter || searching)
    && candidates.status === 'ready'
    && candidates.data.rows.length === fetchLimit
    && candidates.data.rows.length < candidates.data.total;

  function toggleStage(stage: RecruitingStage): void {
    setInterviewsFilter(false);
    setNewTodayFilter(false);
    setStageFilter((prev) => (prev === stage ? null : stage));
  }

  function toggleInterviews(): void {
    setStageFilter(null);
    setNewTodayFilter(false);
    setInterviewsFilter((prev) => !prev);
  }

  function toggleNewToday(): void {
    setStageFilter(null);
    setInterviewsFilter(false);
    setNewTodayFilter((prev) => !prev);
  }

  const filtered = Boolean(stageFilter) || interviewsFilter;
  const emptyLabel = searching ? 'Nobody in the whole database matches.' : newToday && !filtered
    ? 'Nobody new today.'
    : !filtered && query.trim() ? 'Nothing matches.' : filtered ? 'Nobody at this stage.' : 'Nothing matches.';

  // One person's page: the whole story and every action, in place of the
  // list. The list is one click back and costs no request (lib/store.ts).
  if (selectedId !== null) {
    return (
      <div data-testid="recruiting-board">
        <div class="candidate-page-bar">
          <button type="button" class="btn" data-testid="recruiting-back" onClick={backToList}>Back to all candidates</button>
        </div>
        <CandidateCard candidateId={selectedId} archived={selectedArchived} viewer={viewer} onChanged={refreshBoard} />
      </div>
    );
  }

  return (
    <div data-testid="recruiting-board">
      <ClinicTabs
        clinics={clinics}
        active={guideOpen ? RECRUITING_GUIDE_TAB_ID : templatesOpen ? RECRUITING_TEMPLATES_TAB_ID
          : sameNameOpen && canReviewSameName ? RECRUITING_SAME_NAME_TAB_ID : tab}
        onSelect={(code) => { setTemplatesOpen(false); setGuideOpen(false); setSameNameOpen(false); setTab(code); }}
        after={[
          { id: RECRUITING_ALL_TAB_ID, label: 'All clinics' },
          ...(viewer ? [{ id: RECRUITING_TEMPLATES_TAB_ID, label: 'Templates' }] : []),
          { id: RECRUITING_ARCHIVE_TAB_ID, label: 'Archive' },
          ...(canReviewSameName ? [{ id: RECRUITING_SAME_NAME_TAB_ID, label: 'Same name' }] : []),
          { id: RECRUITING_GUIDE_TAB_ID, label: 'Guide' },
        ]}
        onSelectAfter={(id) => {
          if (id === RECRUITING_TEMPLATES_TAB_ID) { setGuideOpen(false); setSameNameOpen(false); setTemplatesOpen(true); return; }
          if (id === RECRUITING_GUIDE_TAB_ID) { setTemplatesOpen(false); setSameNameOpen(false); setGuideOpen(true); return; }
          if (id === RECRUITING_SAME_NAME_TAB_ID) { setTemplatesOpen(false); setGuideOpen(false); setSameNameOpen(true); return; }
          setTemplatesOpen(false);
          setGuideOpen(false);
          setSameNameOpen(false);
          setTab(id);
        }}
        // People.tsx's own clinic strip (the level above this one) already
        // carries Management — a second copy here would put two on screen,
        // same fix PayHours.tsx's clinic switcher applies for the same reason.
        management={false}
        trailing={(
          <>
            <input
              type="search"
              class="people-search"
              data-testid="recruiting-search"
              placeholder="Search everyone: name, phone, email, license"
              value={query}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            />
            {viewer ? (
              <button type="button" class="btn" data-testid="recruiting-add" onClick={() => setAdding((v) => !v)}>
                {adding ? 'Cancel' : 'Add candidate'}
              </button>
            ) : null}
          </>
        )}
      />

      {adding && viewer ? (
        <section class="card" data-testid="recruiting-add-form">
          <AddCandidateForm
            viewer={viewer}
            defaultLocationCode={location !== 'ALL' && location !== 'ARCHIVE' ? (location as LocationCode) : viewer.locationCode}
            onCreated={(created) => { setAdding(false); refreshBoard(); openCandidate(created.id, null); }}
            onExisting={(existingId) => { setAdding(false); openCandidate(existingId, null); }}
          />
        </section>
      ) : null}

      {templatesOpen && viewer ? <RecruitingTemplates viewer={viewer} /> : null}
      {guideOpen ? <RecruitingGuide /> : null}
      {sameNameOpen && canReviewSameName && !templatesOpen && !guideOpen
        ? <SameNameReview onOpenCandidate={(id) => openCandidate(id, null)} />
        : null}

      {!sideOpen ? (<>
      {searching ? (
        <p class="muted" data-testid="recruiting-search-note">
          Search covers everyone in every clinic, including closed candidates.
        </p>
      ) : null}
      {!archive && !searching ? (
        <>
          <DayView
            summary={clinicSummary}
            stageFilter={stageFilter}
            interviewsFilter={interviewsFilter}
            newTodayFilter={newToday}
            onToggleStage={toggleStage}
            onToggleInterviews={toggleInterviews}
            onToggleNewToday={toggleNewToday}
          />
          <PipelineChips summary={clinicSummary} stageFilter={stageFilter} onToggle={toggleStage} />
        </>
      ) : null}

      <CandidateTable
        rows={rows}
        total={total}
        showing={rows.length}
        archive={archive && !searching}
        searching={searching}
        loading={candidates.status === 'loading'}
        emptyLabel={emptyLabel}
        selectedId={selectedId}
        onSelect={(id) => openCandidate(id, searching ? Boolean(rows.find((r) => r.id === id)?.outcome) : archive)}
      />

      {canLoadMore ? (
        <button
          type="button"
          class="btn"
          data-testid="recruiting-load-more"
          onClick={() => setLimit((l) => l + RECRUITING_CANDIDATES_DEFAULT_LIMIT)}
        >
          Show more
        </button>
      ) : null}

      </>) : null}
    </div>
  );
}
